import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getLawyerProfile,
  updateLawyerProfile,
  updateProfile,
} from "@/shared/db";
import {
  ArrowLeftIcon,
  CheckIcon,
  EyeIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { generateLawyerBioWithGroq } from "@/shared/ai";
import { toast } from "sonner";
import {
  Button,
  Card,
  Input,
  Textarea,
  FileUpload,
  FilePreview,
} from "@/shared/ui";
import { DetailPageSkeleton } from "../../../components/skeletons";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { useTranslation } from "react-i18next";

export const EditProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, refreshProfile, loadingProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    full_name: "",
    bar_number: "",
    university: "",
    years_of_experience: 0,
    practice_areas: [] as string[],
    accept_cases: false,
    accept_clients: false,
    hourly_rate: "",
    phone: "",
    bio: "",
    office_address: "",
  });
  const [isExequaturVerified, setIsExequaturVerified] = useState(false);
  const [bioGenerating, setBioGenerating] = useState(false);
  const [showBioLanguagePicker, setShowBioLanguagePicker] = useState(false);

  // File upload hook for signature
  const {
    upload: uploadSignature,
    uploading: uploadingSignature,
    uploadedUrl: uploadedSignatureUrl,
  } = useFileUpload({
    bucket: "lawyer-signatures",
    folder: user?.id || "",
    onSuccess: (url) => {
      setSignatureUrl(url);
      toast.success(t("lawyer.editProfile.signature.uploadSuccess"));
    },
    onError: (error) => {
      toast.error(
        t("lawyer.editProfile.signature.uploadError", {
          message: error.message,
        }),
      );
    },
  });

  // File upload hook for profile image
  const {
    upload: uploadProfileImage,
    uploading: uploadingProfileImage,
    uploadedUrl: uploadedProfileImageUrl,
  } = useFileUpload({
    bucket: "profile-images",
    folder: user?.id || "",
    onSuccess: async (url) => {
      setProfileImageUrl(url);
      // Immediately update profile in database
      if (user) {
        try {
          await updateProfile(user.id, { profile_image_url: url });
          await refreshProfile();
          toast.success(t("lawyer.editProfile.photo.uploadSuccess"));
        } catch (error) {
          console.error("Error updating profile image in database:", error);
          toast.error(t("lawyer.editProfile.photo.updateError"));
        }
      }
    },
    onError: (error) => {
      toast.error(
        t("lawyer.editProfile.photo.uploadError", { message: error.message }),
      );
    },
  });

  const handleProfileImageSelect = async (files: File[]) => {
    if (files[0]) {
      await uploadProfileImage(files[0]);
    }
  };

  const loadProfileData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [lawyerData] = await Promise.all([getLawyerProfile(user.id)]);

      setFormData({
        full_name: profile?.full_name || "",
        bar_number: lawyerData.bar_number || "",
        university: lawyerData.university || "",
        years_of_experience: lawyerData.years_of_experience || 0,
        practice_areas: lawyerData.practice_areas || [],
        accept_cases: lawyerData.accept_cases || false,
        accept_clients: lawyerData.accept_clients || false,
        hourly_rate: lawyerData.hourly_rate?.toString() || "",
        phone: lawyerData.phone || "",
        bio: lawyerData.bio || "",
        office_address: lawyerData.office_address || "",
      });
      setIsExequaturVerified(lawyerData.is_verified === true);

      // Set signature URL if exists
      setSignatureUrl(lawyerData.signature_url || null);
      setProfileImageUrl(profile?.profile_image_url || null);
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error(t("lawyer.editProfile.error"));
    } finally {
      setLoading(false);
    }
  }, [user, profile, t]);

  useEffect(() => {
    if (user && !loadingProfile && profile && loading) {
      loadProfileData();
    }
  }, [user, loadingProfile, profile, loading, loadProfileData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate full_name
    if (!formData.full_name.trim()) {
      newErrors.full_name = t("lawyer.editProfile.errors.nameRequired");
    }

    // Validate university
    if (!formData.university.trim()) {
      newErrors.university = t("lawyer.editProfile.errors.universityRequired");
    }

    // Validate years_of_experience
    if (formData.years_of_experience < 0) {
      newErrors.years_of_experience = t(
        "lawyer.editProfile.errors.experienceInvalid",
      );
    }

    // Validate practice_areas
    if (formData.practice_areas.length === 0) {
      newErrors.practice_areas = t(
        "lawyer.editProfile.errors.practiceAreasRequired",
      );
    }

    // Validate hourly_rate if provided
    if (formData.hourly_rate.trim() !== "") {
      const hourlyRateValue = parseFloat(formData.hourly_rate);
      if (isNaN(hourlyRateValue) || hourlyRateValue <= 0) {
        newErrors.hourly_rate = t(
          "lawyer.editProfile.errors.hourlyRateInvalid",
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    // Validate form before submitting
    if (!validateForm()) {
      toast.error(t("lawyer.editProfile.errors.validationFailed"));
      return;
    }

    try {
      setSaving(true);

      // Update base profile
      await updateProfile(user.id, {
        full_name: formData.full_name,
        profile_image_url:
          profileImageUrl || uploadedProfileImageUrl || undefined,
      });

      // Update lawyer profile (bar_number only if not verified)
      const lawyerUpdates: Parameters<typeof updateLawyerProfile>[1] = {
        university: formData.university,
        years_of_experience: formData.years_of_experience,
        practice_areas: formData.practice_areas,
        accept_cases: formData.accept_cases,
        accept_clients: formData.accept_clients,
        hourly_rate: formData.hourly_rate
          ? parseFloat(formData.hourly_rate)
          : null,
        phone: formData.phone || null,
        bio: formData.bio || null,
        office_address: formData.office_address || null,
        signature_url: signatureUrl || uploadedSignatureUrl || null,
      };
      if (!isExequaturVerified && formData.bar_number !== undefined) {
        lawyerUpdates.bar_number = formData.bar_number?.trim() || null;
      }
      await updateLawyerProfile(user.id, lawyerUpdates);

      await refreshProfile();
      toast.success(t("lawyer.editProfile.success"));
      navigate("/dashboard/lawyer");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(t("lawyer.editProfile.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const togglePracticeArea = (area: string) => {
    setFormData((prev) => ({
      ...prev,
      practice_areas: prev.practice_areas.includes(area)
        ? prev.practice_areas.filter((a) => a !== area)
        : [...prev.practice_areas, area],
    }));
  };

  const generateBioWithLanguage = async (language: "es" | "en") => {
    setBioGenerating(true);
    try {
      const practiceAreaLabels = formData.practice_areas.map(
        (code) => t(`lawyer.step3.areas.${code}`) || code,
      );
      const bio = await generateLawyerBioWithGroq({
        barNumber: formData.bar_number,
        university: formData.university,
        yearsOfExperience: formData.years_of_experience,
        practiceAreas: practiceAreaLabels,
        hourlyRate: formData.hourly_rate || undefined,
        acceptCases: formData.accept_cases,
        acceptClients: formData.accept_clients,
        currentBio: formData.bio || undefined,
        language,
      });
      setFormData((prev) => ({ ...prev, bio }));
      toast.success(t("onboarding.lawyer.step5.aiGeneratedSuccess"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(
        msg.includes("initialized") || msg.includes("not configured")
          ? t("onboarding.lawyer.step5.aiNotAvailable")
          : t("onboarding.lawyer.step5.aiError"),
      );
    } finally {
      setBioGenerating(false);
    }
  };

  const practiceAreas = [
    { code: "civil", name: t("lawyer.step3.areas.civil") },
    { code: "penal", name: t("lawyer.step3.areas.penal") },
    { code: "laboral", name: t("lawyer.step3.areas.laboral") },
    {
      code: "comercial",
      name: t("lawyer.step3.areas.comercial"),
    },
    { code: "fiscal", name: t("lawyer.step3.areas.fiscal") },
    {
      code: "administrativo",
      name: t("lawyer.step3.areas.administrativo"),
    },
  ];

  if (loading) {
    return (
      <DetailPageSkeleton
        showBackButton={true}
        className="bg-linear-to-br from-romance-50 via-white to-romance-100"
      />
    );
  }

  return (
    <div className="mx-auto max-w-full min-w-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <button
            onClick={() => navigate("/dashboard/lawyer")}
            className="flex items-center text-tiber-700 hover:text-juridico mb-4 cursor-pointer transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t("lawyer.editProfile.back")}
          </button>
          <h1 className="text-3xl font-bold text-juridico">
            {t("lawyer.editProfile.title")}
          </h1>
          <p className="text-tiber-700 mt-2">
            {t("lawyer.editProfile.subtitle")}
          </p>
        </div>
        <Button
          onClick={() => navigate(`/lawyers/${user?.id}`)}
          leftIcon={<EyeIcon className="h-5 w-5" />}
        >
          {t("lawyer.editProfile.viewPublic")}
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.sections.photo.title")}
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="shrink-0">
              {profileImageUrl || uploadedProfileImageUrl ? (
                <img
                  className="h-24 w-24 object-cover rounded-full border-2 border-tiber-200"
                  src={profileImageUrl || uploadedProfileImageUrl || ""}
                  alt="Current profile photo"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center border border-tiber-200">
                  <img
                    src="/images/brand/lawgentia-logo-white.png"
                    alt="Lawgentia"
                    className="w-14 h-14 object-contain"
                  />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <FileUpload
                accept={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                maxSize={5 * 1024 * 1024} // 5MB
                onFilesSelected={handleProfileImageSelect}
                disabled={uploadingProfileImage}
                label={t("lawyer.profile.uploadPhoto")}
                helperText={t("lawyer.profile.uploadPhotoHelper")}
                showPreview={false}
              />
            </div>
          </div>
        </Card>

        {/* Personal Info */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t(
              "lawyer.editProfile.sections.personal.title",
              "Personal Information",
            )}
          </h2>
          <div className="space-y-4">
            <div>
              <Input
                label={t("lawyer.editProfile.sections.personal.fullName")}
                value={formData.full_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData({ ...formData, full_name: e.target.value });
                  if (errors.full_name) {
                    setErrors({ ...errors, full_name: "" });
                  }
                }}
                required
                error={errors.full_name}
              />
            </div>
            <div>
              <Input
                label={t("lawyer.editProfile.exequatur")}
                value={formData.bar_number}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!isExequaturVerified) {
                    setFormData({ ...formData, bar_number: e.target.value });
                  }
                }}
                placeholder={t("lawyer.editProfile.exequaturPlaceholder")}
                readOnly={isExequaturVerified}
                disabled={isExequaturVerified}
                helperText={
                  isExequaturVerified
                    ? t("lawyer.editProfile.exequaturVerified")
                    : t("lawyer.editProfile.exequaturEditable")
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label={t("lawyer.editProfile.sections.personal.phone")}
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder={t("lawyer.step2.phonePlaceholder")}
                />
              </div>

              <div>
                <Input
                  label={t(
                    "lawyer.editProfile.sections.personal.officeAddress",
                  )}
                  type="text"
                  value={formData.office_address}
                  onChange={(e) =>
                    setFormData({ ...formData, office_address: e.target.value })
                  }
                  placeholder={t(
                    "lawyer.editProfile.sections.personal.officeAddressPlaceholder",
                  )}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Professional Info */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.sections.professional.title")}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label={t(
                    "lawyer.editProfile.sections.professional.university",
                  )}
                  type="text"
                  value={formData.university}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({ ...formData, university: e.target.value });
                    if (errors.university) {
                      setErrors({ ...errors, university: "" });
                    }
                  }}
                  required
                  error={errors.university}
                />
              </div>

              <div>
                <Input
                  label={t(
                    "lawyer.editProfile.sections.professional.yearsExperience",
                  )}
                  type="number"
                  min="0"
                  value={formData.years_of_experience}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({
                      ...formData,
                      years_of_experience: parseInt(e.target.value) || 0,
                    });
                    if (errors.years_of_experience) {
                      setErrors({ ...errors, years_of_experience: "" });
                    }
                  }}
                  required
                  error={errors.years_of_experience}
                />
              </div>
            </div>

            <div>
              <Input
                label={t("lawyer.editProfile.sections.professional.hourlyRate")}
                type="number"
                min="0"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => {
                  setFormData({ ...formData, hourly_rate: e.target.value });
                  if (errors.hourly_rate) {
                    setErrors({ ...errors, hourly_rate: "" });
                  }
                }}
                placeholder={t(
                  "lawyer.editProfile.sections.professional.hourlyRatePlaceholder",
                )}
                error={errors.hourly_rate}
              />
              <p className="text-sm text-titan mt-1">
                {t(
                  "lawyer.editProfile.sections.professional.hourlyRateHelper",
                  "This will be visible on your public profile (optional)",
                )}
              </p>
            </div>
          </div>
        </Card>

        {/* Practice Areas */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.sections.practiceAreas.title")}
          </h2>
          <p className="text-sm text-tiber-700 mb-4">
            {t("lawyer.editProfile.sections.practiceAreas.description")}
          </p>
          {errors.practice_areas && (
            <p className="text-sm text-red-600 mb-2">{errors.practice_areas}</p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {practiceAreas.map((area) => (
              <button
                key={area.code}
                type="button"
                onClick={() => togglePracticeArea(area.code)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.practice_areas.includes(area.code)
                    ? "border-tiber-500 bg-tiber-50 text-tiber-700"
                    : "border-tiber-200 hover:border-tiber-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{area.name}</span>
                  {formData.practice_areas.includes(area.code) && (
                    <CheckIcon className="h-5 w-5 text-tiber-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Bio - with AI generation like onboarding */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.sections.bio.title")}
          </h2>
          <p className="text-sm text-tiber-600 leading-relaxed mb-2">
            {t("onboarding.lawyer.step5.bioHint")}
          </p>
          <div>
            <Textarea
              label={t("lawyer.editProfile.sections.bio.placeholder")}
              value={formData.bio}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, bio: e.target.value })
              }
              rows={6}
              placeholder={t("lawyer.editProfile.sections.bio.placeholder")}
            />
            <div className="mt-2 flex flex-col items-end gap-2">
              {showBioLanguagePicker ? (
                <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
                  <p className="text-sm text-tiber-700">
                    {t("onboarding.lawyer.step5.aiLanguageQuestion")}
                  </p>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={bioGenerating}
                      onClick={() => {
                        setShowBioLanguagePicker(false);
                        generateBioWithLanguage("es");
                      }}
                      className="cursor-pointer"
                    >
                      {t("onboarding.lawyer.step5.aiLanguageSpanish")}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={bioGenerating}
                      onClick={() => {
                        setShowBioLanguagePicker(false);
                        generateBioWithLanguage("en");
                      }}
                      className="cursor-pointer"
                    >
                      {t("onboarding.lawyer.step5.aiLanguageEnglish")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={bioGenerating}
                      onClick={() => setShowBioLanguagePicker(false)}
                      className="cursor-pointer text-tiber-600"
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={bioGenerating}
                  onClick={() => setShowBioLanguagePicker(true)}
                  leftIcon={
                    bioGenerating ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <SparklesIcon className="w-4 h-4" />
                    )
                  }
                  className="cursor-pointer"
                >
                  {bioGenerating
                    ? t("onboarding.lawyer.step5.aiGenerating")
                    : t("onboarding.lawyer.step5.aiGenerateButton")}
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-titan mt-2">
            {t("lawyer.editProfile.sections.bio.helper")}
          </p>
        </Card>

        {/* Digital Signature */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.signature.title")}
          </h2>
          <p className="text-sm text-tiber-700 mb-4">
            {t("lawyer.editProfile.signature.description")}
          </p>

          {signatureUrl || uploadedSignatureUrl ? (
            <div className="space-y-4">
              <FilePreview
                url={signatureUrl || uploadedSignatureUrl || ""}
                fileName={t("lawyer.editProfile.signature.current")}
                onRemove={() => setSignatureUrl(null)}
              />
            </div>
          ) : (
            <FileUpload
              accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
              maxSize={5 * 1024 * 1024}
              maxFiles={1}
              onFilesSelected={async (files) => {
                if (files[0]) {
                  await uploadSignature(files[0]);
                }
              }}
              disabled={uploadingSignature}
              label={
                uploadingSignature
                  ? t("lawyer.editProfile.signature.uploading")
                  : t("lawyer.editProfile.signature.upload")
              }
              helperText={t("lawyer.editProfile.signature.helperText")}
            />
          )}
        </Card>

        {/* Preferences */}
        <Card className="border-2 border-tiber-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-juridico mb-4">
            {t("lawyer.editProfile.sections.preferences.title")}
          </h2>
          <div className="space-y-4">
            <label className="flex items-start space-x-3 p-4 border-2 border-tiber-200 rounded-lg cursor-pointer hover:bg-romance-50 transition-colors duration-200">
              <input
                type="checkbox"
                checked={formData.accept_cases}
                onChange={(e) =>
                  setFormData({ ...formData, accept_cases: e.target.checked })
                }
                className="mt-1 w-5 h-5"
              />
              <div>
                <div className="font-medium">
                  {t(
                    "lawyer.editProfile.sections.preferences.acceptCases.label",
                  )}
                </div>
                <div className="text-sm text-tiber-700">
                  {t(
                    "lawyer.editProfile.sections.preferences.acceptCases.description",
                  )}
                </div>
              </div>
            </label>

            <label className="flex items-start space-x-3 p-4 border-2 border-tiber-200 rounded-lg cursor-pointer hover:bg-romance-50 transition-colors duration-200">
              <input
                type="checkbox"
                checked={formData.accept_clients}
                onChange={(e) =>
                  setFormData({ ...formData, accept_clients: e.target.checked })
                }
                className="mt-1 w-5 h-5"
              />
              <div>
                <div className="font-medium">
                  {t(
                    "lawyer.editProfile.sections.preferences.acceptClients.label",
                  )}
                </div>
                <div className="text-sm text-tiber-700">
                  {t(
                    "lawyer.editProfile.sections.preferences.acceptClients.description",
                  )}
                </div>
              </div>
            </label>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            onClick={() => navigate("/dashboard/lawyer")}
            variant="secondary"
          >
            {t("lawyer.editProfile.cancel")}
          </Button>
          <Button type="submit" disabled={saving} variant="primary">
            {saving
              ? t("lawyer.editProfile.saving")
              : t("lawyer.editProfile.save")}
          </Button>
        </div>
      </form>
    </div>
  );
};
